import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../lib/animations';
import type { CountryProps } from '../../pages/Cannabis';

/* ── Sub-calculator tabs ── */
const CALCS = [
  { key: 'edible', label: 'Edible Dosage' },
  { key: 'tolerance', label: 'Tolerance Break' },
  { key: 'cost', label: 'Cost Calculator' },
  { key: 'converter', label: 'Unit Converter' },
  { key: 'decarb', label: 'Decarb Guide' },
  { key: 'joint', label: 'Joint Calculator' },
  { key: 'grow', label: 'Grow Light Calc' },
  { key: 'blend', label: 'THC Blender' },
  { key: 'stash', label: 'Stash Planner' },
  { key: 'cooking', label: 'Cooking Converter' },
] as const;

type CalcKey = (typeof CALCS)[number]['key'];

/* ── Edible dosage guidance ── */
interface DoseLevel {
  range: string;
  label: string;
  description: string;
  color: string;
}

const DOSE_LEVELS: DoseLevel[] = [
  { range: '1 - 2.5 mg', label: 'Microdose', description: 'Mild effects, good for beginners. Subtle relief with minimal psychoactive impact.', color: 'text-blue-300' },
  { range: '2.5 - 5 mg', label: 'Low', description: 'Mild euphoria and relaxation. Good for occasional users or low tolerance.', color: 'text-[#4ade80]' },
  { range: '5 - 15 mg', label: 'Moderate', description: 'Stronger euphoria and impairment. Suitable for regular users.', color: 'text-[#d4a574]' },
  { range: '15 - 30 mg', label: 'High', description: 'Strong effects. Only for experienced users with established tolerance.', color: 'text-orange-400' },
  { range: '30 - 50+ mg', label: 'Very High', description: 'Intense effects. Only for users with high tolerance. Use extreme caution.', color: 'text-red-400' },
];

function getDoseLevel(mg: number): DoseLevel | null {
  if (mg <= 0) return null;
  if (mg <= 2.5) return DOSE_LEVELS[0];
  if (mg <= 5) return DOSE_LEVELS[1];
  if (mg <= 15) return DOSE_LEVELS[2];
  if (mg <= 30) return DOSE_LEVELS[3];
  return DOSE_LEVELS[4];
}

/* ── Tolerance break data ── */
interface BreakResult {
  duration: string;
  weeks: { week: string; expectation: string }[];
}

function getToleranceBreak(frequency: string, duration: string): BreakResult {
  const freqScore: Record<string, number> = { daily: 4, several: 3, weekly: 2, occasional: 1 };
  const durScore: Record<string, number> = { month: 1, six_months: 2, year: 3, years: 4 };
  const total = (freqScore[frequency] || 1) + (durScore[duration] || 1);

  if (total <= 3) {
    return {
      duration: '3 - 7 days',
      weeks: [
        { week: 'Days 1-3', expectation: 'Mild cravings, slight irritability. Sleep may be slightly affected.' },
        { week: 'Days 4-7', expectation: 'Most symptoms subside. Appetite and sleep return to normal.' },
      ],
    };
  }
  if (total <= 5) {
    return {
      duration: '2 - 3 weeks',
      weeks: [
        { week: 'Week 1', expectation: 'Noticeable cravings, possible insomnia, vivid dreams, reduced appetite, irritability.' },
        { week: 'Week 2', expectation: 'Cravings decrease. Sleep begins to normalize. Mood stabilizes.' },
        { week: 'Week 3', expectation: 'Most withdrawal effects gone. CB1 receptors begin significant recovery.' },
      ],
    };
  }
  return {
    duration: '4 - 6 weeks',
    weeks: [
      { week: 'Week 1', expectation: 'Strong cravings, insomnia, vivid or unpleasant dreams, irritability, anxiety, reduced appetite.' },
      { week: 'Week 2', expectation: 'Cravings persist but peak subsides. Sweating and headaches may occur. Sleep slowly improves.' },
      { week: 'Week 3', expectation: 'Noticeable improvement. Appetite returns. Mood swings decrease.' },
      { week: 'Week 4', expectation: 'Most physical symptoms resolved. CB1 receptors approaching baseline density.' },
      { week: 'Weeks 5-6', expectation: 'Full receptor reset for most people. Tolerance significantly reduced. Effects will feel much stronger.' },
    ],
  };
}

/* ── Slider input ── */
function SliderInput({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-sm">
        <span className="text-white/60">{label}</span>
        <span className="text-white font-semibold">
          {value}
          {unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-[#4ade80] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-[#4ade80] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-lg"
      />
      <div className="flex justify-between text-[10px] text-white/20">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

/* ── Number input ── */
function NumberInput({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-white/60 text-sm">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={e => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && v >= min && v <= max) onChange(v);
          }}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#4ade80]/40 transition-colors"
        />
        <span className="text-white/40 text-xs whitespace-nowrap">{unit}</span>
      </div>
    </div>
  );
}

/* ── Select input ── */
function SelectInput({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-white/60 text-sm">{label}</label>
      <div className="grid gap-2">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`text-left px-3 py-2 rounded-xl border text-sm transition-all ${
              value === opt.value
                ? 'bg-[#4ade80]/10 border-[#4ade80]/40 text-[#4ade80]'
                : 'bg-white/[0.02] border-white/10 text-white/60 hover:border-white/20'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Edible Calculator ── */
function EdibleCalc() {
  const [grams, setGrams] = useState(3.5);
  const [thc, setThc] = useState(20);
  const [butter, setButter] = useState(1);
  const [servings, setServings] = useState(10);

  const mgPerServing = useMemo(() => {
    if (servings <= 0) return 0;
    return (grams * (thc / 100) * 1000 * 0.88) / servings;
  }, [grams, thc, servings]);

  const totalMg = useMemo(() => grams * (thc / 100) * 1000 * 0.88, [grams, thc]);
  const doseLevel = getDoseLevel(mgPerServing);

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      <motion.div variants={staggerItem} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumberInput label="Flower amount" value={grams} min={0.1} max={100} step={0.5} unit="grams" onChange={setGrams} />
        <NumberInput label="Butter / oil amount" value={butter} min={0.25} max={10} step={0.25} unit="cups" onChange={setButter} />
      </motion.div>

      <motion.div variants={staggerItem}>
        <SliderInput label="THC percentage" value={thc} min={5} max={35} step={1} unit="%" onChange={setThc} />
      </motion.div>

      <motion.div variants={staggerItem}>
        <SliderInput label="Servings" value={servings} min={1} max={50} step={1} unit="" onChange={setServings} />
      </motion.div>

      {/* Results */}
      <motion.div
        variants={staggerItem}
        className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4"
      >
        <h4 className="text-white font-semibold text-sm">Results</h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-white/40 text-xs">Total THC (after decarb)</p>
            <p className="text-white font-bold text-lg">{totalMg.toFixed(1)} mg</p>
          </div>
          <div className="space-y-1">
            <p className="text-white/40 text-xs">Per serving</p>
            <p className={`font-bold text-lg ${doseLevel?.color || 'text-white'}`}>{mgPerServing.toFixed(1)} mg</p>
          </div>
        </div>

        <div className="space-y-1">
          <p className="text-white/40 text-xs">Butter/oil ratio</p>
          <p className="text-white/70 text-sm">{(grams / butter).toFixed(1)}g flower per cup</p>
        </div>

        {doseLevel && (
          <div className={`rounded-xl border border-white/10 p-3 space-y-1`}>
            <p className={`font-semibold text-sm ${doseLevel.color}`}>{doseLevel.label} ({doseLevel.range})</p>
            <p className="text-white/50 text-xs leading-relaxed">{doseLevel.description}</p>
          </div>
        )}
      </motion.div>

      {/* Dose guide reference */}
      <motion.div variants={staggerItem} className="space-y-2">
        <h4 className="text-white/40 text-xs font-semibold uppercase tracking-wider">Dosage Guide</h4>
        <div className="space-y-1.5">
          {DOSE_LEVELS.map(level => (
            <div key={level.label} className="flex items-baseline gap-3 text-xs">
              <span className={`font-semibold w-20 shrink-0 ${level.color}`}>{level.range}</span>
              <span className="text-white/30">{level.label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Tolerance Break Calculator ── */
function ToleranceCalc() {
  const [frequency, setFrequency] = useState('daily');
  const [duration, setDuration] = useState('year');

  const result = useMemo(() => getToleranceBreak(frequency, duration), [frequency, duration]);

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      <motion.div variants={staggerItem}>
        <SelectInput
          label="Usage frequency"
          value={frequency}
          onChange={setFrequency}
          options={[
            { value: 'daily', label: 'Daily' },
            { value: 'several', label: 'Several times per week' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'occasional', label: 'Occasional' },
          ]}
        />
      </motion.div>

      <motion.div variants={staggerItem}>
        <SelectInput
          label="Duration of use"
          value={duration}
          onChange={setDuration}
          options={[
            { value: 'month', label: 'Less than a month' },
            { value: 'six_months', label: '1 - 6 months' },
            { value: 'year', label: '6 - 12 months' },
            { value: 'years', label: '1+ years' },
          ]}
        />
      </motion.div>

      {/* Results */}
      <motion.div
        variants={staggerItem}
        className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4"
      >
        <div className="space-y-1">
          <h4 className="text-white/40 text-xs uppercase tracking-wider font-semibold">Recommended Break</h4>
          <p className="text-[#4ade80] font-bold text-xl">{result.duration}</p>
        </div>

        <div className="space-y-3">
          <h4 className="text-white/40 text-xs uppercase tracking-wider font-semibold">What to Expect</h4>
          {result.weeks.map((w, i) => (
            <div key={i} className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-[#d4a574] text-xs font-semibold">{w.week}</p>
              <p className="text-white/50 text-xs leading-relaxed">{w.expectation}</p>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-[#4ade80]/10 bg-[#4ade80]/5 p-3">
          <p className="text-[#4ade80]/80 text-xs leading-relaxed">
            After your break, start with a much lower dose than usual. Your tolerance will be significantly reduced, and previous doses may feel much stronger.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Cost Calculator ── */
function CostCalc() {
  const [price, setPrice] = useState(10);
  const [sessions, setSessions] = useState(2);
  const [gramsPerSession, setGramsPerSession] = useState(0.3);

  const costs = useMemo(() => {
    const daily = price * sessions * gramsPerSession;
    return {
      daily,
      weekly: daily * 7,
      monthly: daily * 30,
      yearly: daily * 365,
    };
  }, [price, sessions, gramsPerSession]);

  const fmt = (n: number) => n.toFixed(2);

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="space-y-6"
    >
      <motion.div variants={staggerItem}>
        <NumberInput label="Price per gram" value={price} min={1} max={100} step={1} unit="USD" onChange={setPrice} />
      </motion.div>

      <motion.div variants={staggerItem}>
        <SliderInput label="Sessions per day" value={sessions} min={1} max={10} step={1} unit="" onChange={setSessions} />
      </motion.div>

      <motion.div variants={staggerItem}>
        <SliderInput label="Grams per session" value={gramsPerSession} min={0.1} max={2} step={0.1} unit="g" onChange={setGramsPerSession} />
      </motion.div>

      {/* Results */}
      <motion.div
        variants={staggerItem}
        className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4"
      >
        <h4 className="text-white font-semibold text-sm">Your Cannabis Spending</h4>

        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Daily', value: costs.daily, highlight: false },
            { label: 'Weekly', value: costs.weekly, highlight: false },
            { label: 'Monthly', value: costs.monthly, highlight: true },
            { label: 'Yearly', value: costs.yearly, highlight: true },
          ].map(item => (
            <div key={item.label} className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-white/40 text-xs">{item.label}</p>
              <p className={`font-bold text-lg ${item.highlight ? 'text-[#d4a574]' : 'text-white'}`}>
                ${fmt(item.value)}
              </p>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
          <p className="text-white/40 text-xs">Daily consumption</p>
          <p className="text-white/70 text-sm">
            {(sessions * gramsPerSession).toFixed(1)}g per day / {(sessions * gramsPerSession * 7).toFixed(1)}g per week
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Unit Converter ── */
function UnitConverter() {
  const [grams, setGrams] = useState(3.5);
  const oz = grams / 28.3495;
  const lbs = grams / 453.592;
  const eighths = grams / 3.5;
  const quarters = grams / 7;
  const halves = grams / 14;
  const ounces = grams / 28;

  const presets = [
    { label: '1g', val: 1 }, { label: 'Eighth (3.5g)', val: 3.5 }, { label: 'Quarter (7g)', val: 7 },
    { label: 'Half (14g)', val: 14 }, { label: 'Ounce (28g)', val: 28 }, { label: 'QP (112g)', val: 112 },
  ];

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
      <motion.div variants={staggerItem}>
        <NumberInput label="Amount in grams" value={grams} min={0.1} max={10000} step={0.1} unit="g" onChange={setGrams} />
      </motion.div>
      <motion.div variants={staggerItem} className="flex gap-2 flex-wrap">
        {presets.map(p => (
          <button key={p.label} onClick={() => setGrams(p.val)} className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${grams === p.val ? 'bg-[#4ade80]/20 border-[#4ade80]/40 text-[#4ade80]' : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20'}`}>
            {p.label}
          </button>
        ))}
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: 'Grams', val: grams.toFixed(2) + 'g' },
            { label: 'Ounces (exact)', val: oz.toFixed(4) + ' oz' },
            { label: 'Eighths', val: eighths.toFixed(2) },
            { label: 'Quarters', val: quarters.toFixed(2) },
            { label: 'Half ounces', val: halves.toFixed(2) },
            { label: 'Ounces (street)', val: ounces.toFixed(2) + ' oz' },
            { label: 'Pounds', val: lbs.toFixed(4) + ' lb' },
            { label: 'Milligrams', val: (grams * 1000).toFixed(0) + ' mg' },
          ].map(item => (
            <div key={item.label} className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-white/40 text-[10px]">{item.label}</p>
              <p className="text-white font-semibold text-sm">{item.val}</p>
            </div>
          ))}
        </div>
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-xl border border-white/5 bg-white/[0.01] p-4 space-y-2">
        <h4 className="text-white/60 text-xs font-semibold">Common Street Measurements</h4>
        <div className="text-white/40 text-xs space-y-1 leading-relaxed">
          <p><span className="text-white/70 font-semibold">Dime bag:</span> ~1g | <span className="text-white/70 font-semibold">Dub:</span> ~2g | <span className="text-white/70 font-semibold">Eighth:</span> 3.5g</p>
          <p><span className="text-white/70 font-semibold">Quarter:</span> 7g | <span className="text-white/70 font-semibold">Half:</span> 14g | <span className="text-white/70 font-semibold">Zip/Oz:</span> 28g</p>
          <p><span className="text-white/70 font-semibold">QP:</span> 112g (4oz) | <span className="text-white/70 font-semibold">HP:</span> 224g (8oz) | <span className="text-white/70 font-semibold">Pound:</span> 448g (16oz)</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Decarboxylation Guide ── */
function DecarbCalc() {
  const [method, setMethod] = useState('oven');
  const [material, setMaterial] = useState('flower');

  const settings: Record<string, Record<string, { temp: string; time: string; notes: string }>> = {
    flower: {
      oven: { temp: '240°F / 115°C', time: '40 minutes', notes: 'Spread ground flower evenly on parchment-lined baking sheet. Light golden color when done. The most reliable method.' },
      mason: { temp: '240°F / 115°C water bath', time: '90 minutes', notes: 'Seal flower in mason jar, place in boiling water bath. Less smell than oven method. Jar traps terpenes.' },
      sous: { temp: '203°F / 95°C', time: '90 minutes', notes: 'Vacuum seal flower, immerse in water bath at exact temperature. Most precise method — preserves maximum terpenes.' },
      instant: { temp: '240°F / 115°C', time: '40 minutes', notes: 'Set to manual/pressure cook at high. Place flower in jar on trivet with water below. Very consistent results.' },
    },
    kief: {
      oven: { temp: '240°F / 115°C', time: '20-25 minutes', notes: 'Kief decarbs faster due to smaller particle size. Watch closely — it can burn easily. Spread very thin.' },
      mason: { temp: '240°F / 115°C water bath', time: '60 minutes', notes: 'Place kief in sealed mason jar. Shake every 15 minutes to prevent clumping.' },
      sous: { temp: '203°F / 95°C', time: '60 minutes', notes: 'Vacuum seal kief flat. Most consistent results for concentrates.' },
      instant: { temp: '240°F / 115°C', time: '25 minutes', notes: 'Shorter time for kief. Check for color change — should be slightly darker.' },
    },
    concentrate: {
      oven: { temp: '250°F / 121°C', time: '25 minutes', notes: 'Place concentrate in oven-safe dish (silicone works well). It will melt and bubble — bubbling stops when decarb is complete.' },
      mason: { temp: '250°F / 121°C water bath', time: '45 minutes', notes: 'Watch for bubbles through the glass — decarb is done when bubbling stops.' },
      sous: { temp: '203°F / 95°C', time: '60 minutes', notes: 'Flatten concentrate in vacuum bag. Watch for bubble activity through the bag.' },
      instant: { temp: '250°F / 121°C', time: '30 minutes', notes: 'Works well for concentrate decarb. Watch for bubble cessation.' },
    },
  };

  const s2 = settings[material]?.[method] || settings.flower.oven;

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
      <motion.div variants={staggerItem}>
        <SelectInput label="Material type" value={material} onChange={setMaterial} options={[
          { value: 'flower', label: 'Dried Flower' },
          { value: 'kief', label: 'Kief / Dry Sift' },
          { value: 'concentrate', label: 'Concentrate (wax, shatter, rosin)' },
        ]} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <SelectInput label="Method" value={method} onChange={setMethod} options={[
          { value: 'oven', label: 'Oven (most common)' },
          { value: 'mason', label: 'Mason Jar in Water Bath' },
          { value: 'sous', label: 'Sous Vide (most precise)' },
          { value: 'instant', label: 'Instant Pot / Pressure Cooker' },
        ]} />
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <h4 className="text-white font-semibold text-sm">Decarb Settings</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
            <p className="text-white/40 text-[10px]">Temperature</p>
            <p className="text-[#d4a574] font-bold text-lg">{s2.temp}</p>
          </div>
          <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
            <p className="text-white/40 text-[10px]">Time</p>
            <p className="text-[#4ade80] font-bold text-lg">{s2.time}</p>
          </div>
        </div>
        <p className="text-white/50 text-xs leading-relaxed">{s2.notes}</p>
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-xl border border-[#d4a574]/10 bg-[#d4a574]/5 p-3">
        <p className="text-[#d4a574]/80 text-xs leading-relaxed">
          <span className="font-semibold">Why decarb?</span> Raw cannabis contains THCA (non-psychoactive). Heat converts THCA → THC (psychoactive). Without decarboxylation, edibles won't work. The 88% conversion rate accounts for CO2 lost during this process.
        </p>
      </motion.div>
    </motion.div>
  );
}

/* ── Joint Size Calculator ── */
function JointCalc() {
  const [rollType, setRollType] = useState('joint');
  const [size, setSize] = useState('regular');
  const [thcPct, setThcPct] = useState(20);

  const amounts: Record<string, Record<string, { grams: number; label: string; desc: string }>> = {
    joint: {
      pinner: { grams: 0.25, label: 'Pinner', desc: 'Thin, personal joint. Quick solo session.' },
      regular: { grams: 0.5, label: 'Regular', desc: 'Standard joint, good for 1-2 people.' },
      king: { grams: 0.75, label: 'King Size', desc: 'Larger joint using king-size papers. 2-3 people.' },
      cone: { grams: 1.0, label: 'Cone', desc: 'Wide cone shape, holds more. Party size for 3-4 people.' },
    },
    blunt: {
      mini: { grams: 0.7, label: 'Mini Blunt', desc: 'Small cigarillo blunt. 1-2 people.' },
      regular: { grams: 1.5, label: 'Regular Blunt', desc: 'Standard blunt wrap, long-lasting. 2-4 people.' },
      backwood: { grams: 2.0, label: 'Backwood', desc: 'Full tobacco leaf, hand-rolled. Group session.' },
      xl: { grams: 3.5, label: 'XL / Extendo', desc: 'Double wrap or extra-long. Big group or special occasion.' },
    },
    special: {
      cross: { grams: 2.5, label: 'Cross Joint', desc: 'Two joints intersected. 3 burn points. Group piece.' },
      tulip: { grams: 3.0, label: 'Tulip', desc: 'Large cone head on a stem. Showpiece. 4+ people.' },
      cannagar: { grams: 5.0, label: 'Cannagar', desc: 'Cannabis cigar. Burns 1-4 hours. Major group session.' },
      thai: { grams: 4.0, label: 'Thai Stick', desc: 'Buds on stick wrapped in leaves. Ancient technique.' },
    },
  };

  const typeOpts = { joint: 'Joints', blunt: 'Blunts', special: 'Special Rolls' };
  const sizeOpts = amounts[rollType] || amounts.joint;
  const selected = sizeOpts[size] || Object.values(sizeOpts)[0];
  const totalMg = selected.grams * (thcPct / 100) * 1000;
  const inhaled = totalMg * 0.25; // ~25% of THC is actually absorbed when smoking

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
      <motion.div variants={staggerItem}>
        <SelectInput label="Roll type" value={rollType} onChange={(v) => { setRollType(v); setSize(Object.keys(amounts[v] || amounts.joint)[0]); }} options={Object.entries(typeOpts).map(([v, l]) => ({ value: v, label: l }))} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <SelectInput label="Size" value={size} onChange={setSize} options={Object.entries(sizeOpts).map(([v, d]) => ({ value: v, label: `${d.label} (${d.grams}g)` }))} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <SliderInput label="THC percentage" value={thcPct} min={5} max={35} step={1} unit="%" onChange={setThcPct} />
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <h4 className="text-white font-semibold text-sm">{selected.label}</h4>
        <p className="text-white/50 text-xs">{selected.desc}</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
            <p className="text-white/40 text-[10px]">Cannabis needed</p>
            <p className="text-white font-bold">{selected.grams}g</p>
          </div>
          <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
            <p className="text-white/40 text-[10px]">Total THC</p>
            <p className="text-[#d4a574] font-bold">{totalMg.toFixed(0)}mg</p>
          </div>
          <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
            <p className="text-white/40 text-[10px]">THC absorbed (~25%)</p>
            <p className="text-[#4ade80] font-bold">{inhaled.toFixed(0)}mg</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Grow Light Calculator ── */
function GrowLightCalc() {
  const [length, setLength] = useState(4);
  const [width, setWidth] = useState(4);
  const [stage, setStage] = useState('flower');
  const [lightType, setLightType] = useState('led');

  const sqft = length * width;
  const wpsfTarget = stage === 'veg' ? 25 : 35; // watts per sqft
  const hpsMultiplier = lightType === 'hps' ? 1.5 : 1; // HPS needs more watts for same output
  const totalWatts = sqft * wpsfTarget * hpsMultiplier;
  const ppfd = stage === 'veg' ? '400-600' : '600-1000';
  const plants = Math.floor(sqft / 4); // ~4 sqft per plant in pots
  const dailyKwh = (totalWatts / 1000) * (stage === 'veg' ? 18 : 12);
  const monthlyKwh = dailyKwh * 30;
  const monthlyCost = monthlyKwh * 0.12; // avg US electricity rate

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
      <motion.div variants={staggerItem} className="grid grid-cols-2 gap-4">
        <NumberInput label="Length" value={length} min={1} max={20} step={1} unit="feet" onChange={setLength} />
        <NumberInput label="Width" value={width} min={1} max={20} step={1} unit="feet" onChange={setWidth} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <SelectInput label="Growth stage" value={stage} onChange={setStage} options={[
          { value: 'veg', label: 'Vegetative (18/6 light cycle)' },
          { value: 'flower', label: 'Flowering (12/12 light cycle)' },
        ]} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <SelectInput label="Light type" value={lightType} onChange={setLightType} options={[
          { value: 'led', label: 'LED (recommended)' },
          { value: 'hps', label: 'HPS (High Pressure Sodium)' },
        ]} />
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <h4 className="text-white font-semibold text-sm">Your Grow Space: {length}' x {width}' ({sqft} sq ft)</h4>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Wattage needed', val: `${totalWatts.toFixed(0)}W`, color: 'text-[#d4a574]' },
            { label: 'Target PPFD', val: `${ppfd} µmol`, color: 'text-[#4ade80]' },
            { label: 'Plants (approx)', val: `${plants} plants`, color: 'text-white' },
            { label: 'Daily power', val: `${dailyKwh.toFixed(1)} kWh`, color: 'text-white' },
            { label: 'Monthly power', val: `${monthlyKwh.toFixed(0)} kWh`, color: 'text-[#d4a574]' },
            { label: 'Monthly cost (est)', val: `$${monthlyCost.toFixed(2)}`, color: 'text-[#4ade80]' },
          ].map(item => (
            <div key={item.label} className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-white/40 text-[10px]">{item.label}</p>
              <p className={`font-bold text-sm ${item.color}`}>{item.val}</p>
            </div>
          ))}
        </div>
        <p className="text-white/30 text-[10px]">Based on {lightType === 'led' ? '35' : '50'}W/sqft for flower, {lightType === 'led' ? '25' : '37'}W/sqft for veg. Electricity at $0.12/kWh avg.</p>
      </motion.div>
    </motion.div>
  );
}

/* ── THC Blender ── */
function BlendCalc() {
  const [strain1Thc, setStrain1Thc] = useState(25);
  const [strain1Grams, setStrain1Grams] = useState(2);
  const [strain2Thc, setStrain2Thc] = useState(10);
  const [strain2Grams, setStrain2Grams] = useState(1);
  const [strain1Cbd, setStrain1Cbd] = useState(0.5);
  const [strain2Cbd, setStrain2Cbd] = useState(15);

  const totalGrams = strain1Grams + strain2Grams;
  const blendedThc = totalGrams > 0 ? (strain1Thc * strain1Grams + strain2Thc * strain2Grams) / totalGrams : 0;
  const blendedCbd = totalGrams > 0 ? (strain1Cbd * strain1Grams + strain2Cbd * strain2Grams) / totalGrams : 0;
  const ratio = blendedCbd > 0 ? (blendedThc / blendedCbd).toFixed(1) : '∞';

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <h4 className="text-[#4ade80] text-xs font-semibold">Strain 1</h4>
        <div className="grid grid-cols-3 gap-3">
          <NumberInput label="Grams" value={strain1Grams} min={0.1} max={28} step={0.1} unit="g" onChange={setStrain1Grams} />
          <NumberInput label="THC %" value={strain1Thc} min={0} max={35} step={0.5} unit="%" onChange={setStrain1Thc} />
          <NumberInput label="CBD %" value={strain1Cbd} min={0} max={25} step={0.5} unit="%" onChange={setStrain1Cbd} />
        </div>
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <h4 className="text-[#d4a574] text-xs font-semibold">Strain 2</h4>
        <div className="grid grid-cols-3 gap-3">
          <NumberInput label="Grams" value={strain2Grams} min={0.1} max={28} step={0.1} unit="g" onChange={setStrain2Grams} />
          <NumberInput label="THC %" value={strain2Thc} min={0} max={35} step={0.5} unit="%" onChange={setStrain2Thc} />
          <NumberInput label="CBD %" value={strain2Cbd} min={0} max={25} step={0.5} unit="%" onChange={setStrain2Cbd} />
        </div>
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-[#4ade80]/20 bg-[#4ade80]/5 p-5 space-y-4">
        <h4 className="text-white font-semibold text-sm">Blended Result ({totalGrams.toFixed(1)}g)</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <p className="text-white/40 text-[10px]">Blended THC</p>
            <p className="text-[#4ade80] font-bold text-xl">{blendedThc.toFixed(1)}%</p>
          </div>
          <div className="text-center">
            <p className="text-white/40 text-[10px]">Blended CBD</p>
            <p className="text-[#d4a574] font-bold text-xl">{blendedCbd.toFixed(1)}%</p>
          </div>
          <div className="text-center">
            <p className="text-white/40 text-[10px]">THC:CBD Ratio</p>
            <p className="text-white font-bold text-xl">{ratio}:1</p>
          </div>
        </div>
        <p className="text-white/40 text-xs leading-relaxed">
          {blendedThc > 20 ? 'High THC blend — strong psychoactive effects.' :
           blendedCbd > blendedThc ? 'CBD-dominant blend — therapeutic with minimal high.' :
           Math.abs(blendedThc - blendedCbd) < 5 ? 'Balanced blend — moderate effects with good synergy (entourage effect).' :
           'Moderate blend — psychoactive with some CBD balance.'}
        </p>
      </motion.div>
    </motion.div>
  );
}

/* ── Stash Planner ── */
function StashPlanner() {
  const [stashGrams, setStashGrams] = useState(7);
  const [sessionsPerDay, setSessionsPerDay] = useState(2);
  const [gramsPerSession, setGramsPerSession] = useState(0.3);

  const dailyUse = sessionsPerDay * gramsPerSession;
  const daysLeft = dailyUse > 0 ? stashGrams / dailyUse : 0;
  const weeksLeft = daysLeft / 7;
  const runOutDate = new Date();
  runOutDate.setDate(runOutDate.getDate() + Math.floor(daysLeft));
  const totalSessions = dailyUse > 0 ? stashGrams / gramsPerSession : 0;

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
      <motion.div variants={staggerItem}>
        <NumberInput label="Current stash" value={stashGrams} min={0.1} max={500} step={0.5} unit="grams" onChange={setStashGrams} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <SliderInput label="Sessions per day" value={sessionsPerDay} min={1} max={10} step={1} unit="" onChange={setSessionsPerDay} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <SliderInput label="Grams per session" value={gramsPerSession} min={0.1} max={2} step={0.1} unit="g" onChange={setGramsPerSession} />
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <h4 className="text-white font-semibold text-sm">Stash Forecast</h4>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Daily usage', val: `${dailyUse.toFixed(1)}g/day`, color: 'text-white' },
            { label: 'Days remaining', val: `${Math.floor(daysLeft)} days`, color: daysLeft < 3 ? 'text-red-400' : daysLeft < 7 ? 'text-[#d4a574]' : 'text-[#4ade80]' },
            { label: 'Weeks remaining', val: `${weeksLeft.toFixed(1)} weeks`, color: 'text-white' },
            { label: 'Total sessions left', val: `${Math.floor(totalSessions)}`, color: 'text-white' },
          ].map(item => (
            <div key={item.label} className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-white/40 text-[10px]">{item.label}</p>
              <p className={`font-bold text-sm ${item.color}`}>{item.val}</p>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
          <p className="text-white/40 text-[10px]">Estimated run-out date</p>
          <p className={`font-bold text-sm ${daysLeft < 3 ? 'text-red-400' : 'text-white'}`}>{runOutDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        {daysLeft < 3 && (
          <div className="rounded-xl border border-red-400/20 bg-red-400/5 p-3">
            <p className="text-red-400/80 text-xs">Time to re-up! Your stash won't last the week at this rate.</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

/* ── Cooking Converter ── */
function CookingConverter() {
  const [fromType, setFromType] = useState('butter');
  const [amount, setAmount] = useState(1);

  const conversions: Record<string, { label: string; toOil: number; toButter: number; notes: string }> = {
    butter: { label: 'Cannabutter (cups)', toOil: 0.875, toButter: 1, notes: 'Standard base for most baked goods. 1 cup butter = 2 sticks = 227g.' },
    coconut: { label: 'Coconut Oil (cups)', toOil: 1, toButter: 1.14, notes: 'Higher fat content absorbs more THC. Best for capsules and gummies. Vegan-friendly.' },
    olive: { label: 'Olive Oil (cups)', toOil: 1, toButter: 1.14, notes: 'Best for savory cooking — pasta, salad dressings. Don\'t use for high-heat baking.' },
  };

  const from = conversions[fromType];

  const tbsp = amount * 16;
  const ml = amount * 236.6;
  const grams = fromType === 'butter' ? amount * 227 : amount * 218;

  return (
    <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
      <motion.div variants={staggerItem}>
        <SelectInput label="Converting from" value={fromType} onChange={setFromType} options={Object.entries(conversions).map(([v, d]) => ({ value: v, label: d.label }))} />
      </motion.div>
      <motion.div variants={staggerItem}>
        <NumberInput label="Amount" value={amount} min={0.25} max={10} step={0.25} unit="cups" onChange={setAmount} />
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
        <h4 className="text-white font-semibold text-sm">Conversions for {amount} cup(s) {from.label.split(' ')[0]}</h4>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Tablespoons', val: `${tbsp.toFixed(0)} tbsp` },
            { label: 'Milliliters', val: `${ml.toFixed(0)} ml` },
            { label: 'Grams (weight)', val: `${grams.toFixed(0)}g` },
            { label: 'As butter', val: `${(amount * from.toButter).toFixed(2)} cups` },
            { label: 'As coconut oil', val: `${(amount * from.toOil).toFixed(2)} cups` },
            { label: 'As olive oil', val: `${(amount * from.toOil).toFixed(2)} cups` },
          ].map(item => (
            <div key={item.label} className="rounded-xl border border-white/5 bg-white/[0.01] p-3 space-y-1">
              <p className="text-white/40 text-[10px]">{item.label}</p>
              <p className="text-white font-semibold text-sm">{item.val}</p>
            </div>
          ))}
        </div>
        <p className="text-white/40 text-xs leading-relaxed">{from.notes}</p>
      </motion.div>
      <motion.div variants={staggerItem} className="rounded-xl border border-white/5 bg-white/[0.01] p-4 space-y-2">
        <h4 className="text-white/60 text-xs font-semibold">Infusion Ratios (flower per cup of fat)</h4>
        <div className="text-white/40 text-xs space-y-1 leading-relaxed">
          <p><span className="text-[#4ade80] font-semibold">Mild:</span> 3.5g (1/8 oz) per cup — ~30mg THC/tbsp</p>
          <p><span className="text-[#d4a574] font-semibold">Standard:</span> 7g (1/4 oz) per cup — ~60mg THC/tbsp</p>
          <p><span className="text-red-400 font-semibold">Strong:</span> 14g (1/2 oz) per cup — ~120mg THC/tbsp</p>
          <p className="text-white/30 mt-1">Based on 20% THC flower with 88% decarb efficiency.</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ── Main component ── */
export default function DosageCalcTab(_props: CountryProps) {
  const [activeCalc, setActiveCalc] = useState<CalcKey>('edible');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-white">Cannabis Calculators</h2>
        <p className="text-white/40 text-sm">Tools for responsible and informed cannabis use.</p>
      </div>

      {/* Sub-tab pills */}
      <div className="flex gap-2 flex-wrap">
        {CALCS.map(c => (
          <button
            key={c.key}
            onClick={() => setActiveCalc(c.key)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-colors ${
              activeCalc === c.key
                ? 'bg-[#4ade80]/20 border-[#4ade80]/40 text-[#4ade80]'
                : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20 hover:text-white/70'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Active calculator */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCalc}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="max-w-lg"
        >
          {activeCalc === 'edible' && <EdibleCalc />}
          {activeCalc === 'tolerance' && <ToleranceCalc />}
          {activeCalc === 'cost' && <CostCalc />}
          {activeCalc === 'converter' && <UnitConverter />}
          {activeCalc === 'decarb' && <DecarbCalc />}
          {activeCalc === 'joint' && <JointCalc />}
          {activeCalc === 'grow' && <GrowLightCalc />}
          {activeCalc === 'blend' && <BlendCalc />}
          {activeCalc === 'stash' && <StashPlanner />}
          {activeCalc === 'cooking' && <CookingConverter />}
        </motion.div>
      </AnimatePresence>

      {/* Disclaimer */}
      <p className="text-white/20 text-[10px] leading-relaxed max-w-lg">
        These calculators are for informational purposes only and should not be considered medical advice. Individual responses to cannabis vary widely. Always start with a low dose and consult a healthcare professional for medical guidance.
      </p>
    </div>
  );
}
